import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Building, Mail, Phone, Send, ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  organisation_details: z.string().min(1, "Organisation details are required"),
  email: z.string().email("Must be a valid email address"),
  other_email: z.string().email("Must be a valid email address").optional().or(z.literal("")),
  phone_number_1: z.string().min(1, "Phone number 1 is required"),
  phone_number_2: z.string().optional(),
  message: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function CorporatePage() {
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    try {
      // Save to database
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert({
          name: data.name,
          organisation_details: data.organisation_details,
          email: data.email,
          other_email: data.other_email || null,
          phone_number_1: data.phone_number_1,
          phone_number_2: data.phone_number_2 || null,
          message: data.message || null,
        });

      if (dbError) throw dbError;

      // Send email notification (this would be implemented via edge function)
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          ...data,
          to: 'jaiveagit@gmail.com',
        },
      });

      if (emailError) {
        console.warn('Email notification failed:', emailError);
        // Don't fail the whole submission if email fails
      }

      toast({
        title: "Success!",
        description: "Your message has been sent successfully. We'll get back to you soon.",
      });
      
      form.reset();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Corporate Contact - Partner with Us"
        description="Get in touch with our corporate team for advertising opportunities and partnerships. Contact us for banner placements and commercial collaborations."
        keywords="corporate contact, advertising, partnerships, banner ads, commercial opportunities"
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link to="/">
              <Button variant="outline" size="sm" className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold tracking-tight">Corporate Partnerships</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Partner with us to reach thousands of travelers and aviation enthusiasts. 
                Get in touch for advertising opportunities and commercial collaborations.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card>
              <CardHeader style={{ backgroundColor: 'orange' }}>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Get in Touch
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter your full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="organisation_details"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organisation Details *</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us about your organisation, industry, and what you do"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primary Email *</FormLabel>
                            <FormControl>
                              <Input placeholder="your@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="other_email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Alternative Email</FormLabel>
                            <FormControl>
                              <Input placeholder="alternative@email.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="phone_number_1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primary Phone *</FormLabel>
                            <FormControl>
                              <Input placeholder="+1 (555) 123-4567" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone_number_2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Alternative Phone</FormLabel>
                            <FormControl>
                              <Input placeholder="+1 (555) 987-6543" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Message</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us more about your advertising goals, budget, or any specific requirements"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Information Panel */}
            <div className="space-y-6">
              <Card>
                <CardHeader style={{ backgroundColor: 'orange' }}>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Why Partner with Us?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">High Traffic Volume</h4>
                    <p className="text-sm text-muted-foreground">
                      Reach thousands of daily visitors checking flight information and travel updates.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Targeted Audience</h4>
                    <p className="text-sm text-muted-foreground">
                      Connect with travelers, aviation enthusiasts, and business professionals.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Premium Placement</h4>
                    <p className="text-sm text-muted-foreground">
                      Your ads are displayed prominently across our flight information platform.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Flexible Campaigns</h4>
                    <p className="text-sm text-muted-foreground">
                      Choose campaign duration, targeting options, and monitor performance.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader style={{ backgroundColor: 'orange' }}>
                  <CardTitle>Advertising Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold">Leaderboard Banners</h4>
                    <p className="text-sm text-muted-foreground">728 x 90 pixel premium placement</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold">Sponsored Content</h4>
                    <p className="text-sm text-muted-foreground">Native advertising opportunities</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold">Custom Packages</h4>
                    <p className="text-sm text-muted-foreground">Tailored solutions for your needs</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader style={{ backgroundColor: 'orange' }}>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Direct Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <strong>Email:</strong> jaiveagit@gmail.com
                    </p>
                    <p className="text-sm text-muted-foreground">
                      We typically respond within 24 hours during business days.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}